import { useEffect, useState } from "react"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { Badge } from "@/components/ui/badge"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Plus, Trash2, Pencil } from "lucide-react"
import { MODULE_CATALOG } from "@/lib/assistant/modules"
import type { AssistantSkill } from "@/lib/assistant/store"
import { useAssistant } from "./AssistantProvider"

type Draft = Omit<AssistantSkill, "id"> & { id?: string }

const EMPTY: Draft = { name: "", description: "", instructions: "", scope: "global", modules: [], trigger: "auto", enabled: true }

function SkillForm({ initial, onSave, onCancel }: { initial: Draft; onSave: (d: Draft) => void; onCancel: () => void }) {
  const [d, setD] = useState<Draft>(initial)
  const valid = d.name.trim() && d.instructions.trim() && (d.scope === "global" || d.modules.length > 0)
  return (
    <div className="space-y-3 rounded-lg border p-3">
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <Label htmlFor="skill-name" className="text-xs">Name</Label>
          <Input
            id="skill-name" value={d.name} placeholder="weekly-report"
            onChange={e => setD({ ...d, name: e.target.value.replace(/\s+/g, "-") })}
            className="h-8 text-sm"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Runs</Label>
          <Select value={d.trigger} onValueChange={v => setD({ ...d, trigger: v as Draft["trigger"] })}>
            <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="auto">Automatically, when relevant</SelectItem>
              <SelectItem value="slash">Only when someone types /{d.name || "name"}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="space-y-1">
        <Label htmlFor="skill-description" className="text-xs">Short description</Label>
        <Input id="skill-description" value={d.description} onChange={e => setD({ ...d, description: e.target.value })} className="h-8 text-sm" />
      </div>
      <div className="space-y-1">
        <Label htmlFor="skill-instructions" className="text-xs">Instructions</Label>
        <Textarea id="skill-instructions" value={d.instructions} rows={6} onChange={e => setD({ ...d, instructions: e.target.value })} className="text-sm" />
      </div>
      <div className="space-y-2">
        <Label className="text-xs">Applies</Label>
        <Select value={d.scope} onValueChange={v => setD({ ...d, scope: v as Draft["scope"] })}>
          <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="global">Everywhere</SelectItem>
            <SelectItem value="modules">Only in chosen areas</SelectItem>
          </SelectContent>
        </Select>
        {d.scope === "modules" && (
          <div className="grid grid-cols-2 gap-1.5">
            {MODULE_CATALOG.map(m => (
              <label key={m.id} className="flex items-center gap-2 text-xs">
                <Checkbox
                  checked={d.modules.includes(m.id)}
                  onCheckedChange={c => setD({ ...d, modules: c ? [...d.modules, m.id] : d.modules.filter(x => x !== m.id) })}
                />
                {m.title}
              </label>
            ))}
          </div>
        )}
      </div>
      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2 text-xs">
          <Switch checked={d.enabled} onCheckedChange={v => setD({ ...d, enabled: v })} /> On
        </label>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={onCancel}>Cancel</Button>
          <Button size="sm" disabled={!valid} onClick={() => onSave({ ...d, name: d.name.trim(), modules: d.scope === "global" ? [] : d.modules })}>Save skill</Button>
        </div>
      </div>
    </div>
  )
}

export function SkillsDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const a = useAssistant()
  const [editing, setEditing] = useState<Draft | null>(null)
  useEffect(() => { if (!open) setEditing(null) }, [open])
  const areaLabel = (id: string) => MODULE_CATALOG.find(m => m.id === id)?.title ?? id

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Team skills</DialogTitle>
          <DialogDescription>
            Instructions the assistant follows for your team, everywhere or only in chosen areas.
            {!a.canConfigure && " Your role can view skills but not change them."}
          </DialogDescription>
        </DialogHeader>
        {a.persistence === "missing" && (
          <p className="text-xs text-amber-700">Skills can't be saved until the assistant database tables are created.</p>
        )}
        <div className="space-y-2">
          {a.skills.length === 0 && !editing && <p className="text-sm text-muted-foreground">No skills yet.</p>}
          {a.skills.map(s => (
            editing?.id === s.id ? null : (
              <div key={s.id} className="flex items-start gap-2 rounded-lg border p-3">
                <div className="min-w-0 flex-1 space-y-1">
                  <p className="text-sm font-medium">
                    {s.trigger === "slash" ? `/${s.name}` : s.name}
                    {!s.enabled && <Badge variant="outline" className="ml-2 px-1 py-0 text-[10px]">off</Badge>}
                  </p>
                  {s.description && <p className="text-xs text-muted-foreground">{s.description}</p>}
                  <p className="text-[11px] text-muted-foreground">
                    {s.scope === "global" ? "Everywhere" : s.modules.map(areaLabel).join(", ")} · {s.trigger === "slash" ? "on request" : "automatic"}
                  </p>
                </div>
                {a.canConfigure && (
                  <>
                    <Button variant="ghost" size="icon" className="h-7 w-7" title="Edit" onClick={() => setEditing(s)}>
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7" title="Delete" onClick={() => void a.deleteSkill(s.id)}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </>
                )}
              </div>
            )
          ))}
          {editing && (
            <SkillForm
              key={editing.id ?? "new"}
              initial={editing}
              onCancel={() => setEditing(null)}
              onSave={async d => { await a.saveSkill(d); setEditing(null) }}
            />
          )}
          {a.canConfigure && !editing && (
            <Button variant="outline" size="sm" onClick={() => setEditing(EMPTY)}>
              <Plus className="mr-1 h-4 w-4" /> Add skill
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

export function MemoryDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const a = useAssistant()
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>What the assistant remembers</DialogTitle>
          <DialogDescription>
            Notes it saved during chats. Yours are private; team notes are shared with everyone on the team.
          </DialogDescription>
        </DialogHeader>
        {a.memory.length === 0 && <p className="text-sm text-muted-foreground">Nothing saved yet. Ask it to remember something.</p>}
        <div className="space-y-1.5">
          {a.memory.map(n => (
            <div key={n.id} className="flex items-start gap-2 rounded-md border p-2">
              <p className="min-w-0 flex-1 text-sm">{n.content}</p>
              {n.shared && <Badge variant="secondary" className="shrink-0 px-1 py-0 text-[10px]">team</Badge>}
              {(!n.shared || a.canConfigure) && (
                <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0" title="Forget" onClick={() => void a.forgetMemory(n.id)}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  )
}
